// "Redactar con IA" — a shared button + tiny modal for EVERY template/message
// editor, so the AI-compose UX is identical everywhere (Lissette 2026-07-24:
// quería redactar con IA en todos los editores). Ask what the message is about,
// call the same /ai/draft-email endpoint MassEmailView uses, and hand the
// editor back { subject, body, lang } to drop into its fields.
//
// Bilingual editors (greeting, tickets) render this once and fill the side that
// matches the chosen language; the editor's existing "Traducir con IA" button
// fills the other side. Plain-text editors get plaintext; HTML editors get HTML.

import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { toast } from './Toast.jsx';

const Z = 10001; // above the greeting/edit modals (z 9999) and ui.jsx Modal

function htmlToText(s) {
  return String(s || '')
    .replace(/<\/p>\s*<p[^>]*>/gi, '\n\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .trim();
}
function textToHtml(s) {
  return String(s || '').split(/\n{2,}/).map((p) => `<p>${p.trim().replace(/\n/g, '<br>')}</p>`).join('\n');
}

export function AiDraftButton({
  lang = 'es', onResult, contextHint = '', bodyAsHtml = false,
  label, compact = false, style,
}) {
  const es = lang === 'es';
  const [open, setOpen] = useState(false);
  const [purpose, setPurpose] = useState('');
  const [draftLang, setDraftLang] = useState(lang === 'es' ? 'es' : 'en');
  const [busy, setBusy] = useState(false);

  const run = async () => {
    const p = purpose.trim();
    if (!p) { toast.error(es ? 'Escribe de qué trata el mensaje' : 'Say what the message is about'); return; }
    setBusy(true);
    try {
      const { aiDraftEmail } = await import('../api.js');
      const out = await aiDraftEmail({
        purpose: `${p}${contextHint ? `\n\n${contextHint}` : ''}`,
        tone: 'friendly',
        lang: draftLang === 'es' ? 'es' : 'en',
      });
      let body = out?.body || '';
      const looksHtml = /<[a-z][\s\S]*>/i.test(body);
      if (bodyAsHtml && !looksHtml) body = textToHtml(body);
      else if (!bodyAsHtml && looksHtml) body = htmlToText(body);
      onResult({ subject: out?.subject || '', body, lang: draftLang });
      setOpen(false);
      setPurpose('');
      toast.success(es ? 'Borrador listo — revísalo antes de enviar' : 'Draft ready — review before sending');
    } catch (err) {
      toast.error((es ? 'La IA no pudo redactar: ' : 'AI could not draft: ') + (err.message || err));
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        type="button" disabled={busy} onClick={() => setOpen(true)}
        title={es ? 'Escribe de qué va el mensaje y la IA redacta el asunto y el cuerpo' : 'Say what the message is about and AI writes subject + body'}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          background: 'rgba(167,139,250,.12)', border: '1px solid rgba(167,139,250,.4)', color: '#a78bfa',
          fontSize: compact ? '.68rem' : '.72rem', fontWeight: 700, padding: compact ? '.22rem .5rem' : '.3rem .6rem',
          borderRadius: 7, cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit', ...style,
        }}
      >
        <Sparkles size={compact ? 11 : 13} /> {busy ? (es ? 'Redactando…' : 'Writing…') : (label || (es ? 'Redactar con IA' : 'Write with AI'))}
      </button>

      {open && (
        <div onClick={() => !busy && setOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: Z, background: 'rgba(0,0,0,.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: 'var(--c-bg2,#0b1622)', border: '1px solid var(--c-border2,#1f3f60)', borderRadius: 14, padding: '1.25rem', maxWidth: 460, width: '100%' }}>
            <div style={{ fontWeight: 800, fontSize: '.95rem', color: 'var(--c-white,#eef6ff)', marginBottom: '.6rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Sparkles size={16} style={{ color: '#a78bfa' }} /> {es ? 'Redactar con IA' : 'Write with AI'}
            </div>
            <label style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', display: 'block', marginBottom: 4 }}>
              {es ? '¿De qué trata el mensaje? (una línea)' : 'What is the message about? (one line)'}
            </label>
            <textarea autoFocus value={purpose} onChange={(e) => setPurpose(e.target.value)} rows={3}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) run(); }}
              placeholder={es ? 'Ej.: recordar al cliente que su póliza vence pronto y ofrecer renovar' : 'e.g. remind the client their policy expires soon and offer to renew'}
              style={{ width: '100%', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.55rem .7rem', color: 'var(--c-white,#eef6ff)', fontSize: '.82rem', outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: '.7rem' }}>
              <span style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)' }}>{es ? 'Idioma:' : 'Language:'}</span>
              {[{ id: 'es', label: '🇪🇸 ES' }, { id: 'en', label: '🇺🇸 EN' }].map((L) => {
                const on = draftLang === L.id;
                return <button key={L.id} type="button" onClick={() => setDraftLang(L.id)}
                  style={{ fontSize: '.72rem', fontWeight: 800, padding: '.28rem .6rem', borderRadius: 7, cursor: 'pointer', border: `1px solid ${on ? '#a78bfa' : 'var(--c-border,#1a3550)'}`, background: on ? 'rgba(167,139,250,.14)' : 'var(--c-surface2,#112538)', color: on ? '#a78bfa' : 'var(--c-text3,#45688a)' }}>{L.label}</button>;
              })}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                <button type="button" onClick={() => setOpen(false)} disabled={busy}
                  style={{ padding: '.4rem .8rem', borderRadius: 8, border: '1px solid var(--c-border,#1a3550)', background: 'transparent', color: 'var(--c-text2,#7aa3c0)', cursor: 'pointer', fontSize: '.78rem' }}>{es ? 'Cancelar' : 'Cancel'}</button>
                <button type="button" onClick={run} disabled={busy}
                  style={{ padding: '.4rem .9rem', borderRadius: 8, border: 'none', background: '#a78bfa', color: '#1a1030', fontWeight: 800, cursor: busy ? 'wait' : 'pointer', fontSize: '.78rem' }}>{busy ? (es ? 'Redactando…' : 'Writing…') : (es ? 'Generar' : 'Generate')}</button>
              </div>
            </div>
            <div style={{ fontSize: '.64rem', color: 'var(--c-text3,#45688a)', marginTop: '.6rem' }}>
              {es ? 'La IA escribe un borrador; revísalo y edítalo antes de usar. Usa {{firstName}} para el nombre del cliente.' : 'AI writes a draft; review and edit before using. Use {{firstName}} for the client name.'}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
